import { app, ojs, payload, state } from "modular-openscriptjs";
import {
    Middleware,
    PAGES,
    analytics,
    api,
    uc,
    gc,
    dc,
    resolveCurrentPageUsing,
} from "./declarations";
import {
    empty,
    fireEvent,
    leaveEchoChannel,
    listenToEchoEvent,
    scrollToTop,
    redirect,
    setSearchQuery,
    setCurrentLocationCoordinates,
    setActiveNav,
} from "./helpers";
import App from "./components/App";

// Page Imports
import Index from "./components/Pages/Index";
import LoginPage from "./components/Pages/LoginPage";
import SignUp from "./components/Pages/Users/SignUp";
import CartPage from "./components/Pages/CartPage";
import Page404 from "./components/Pages/Page404";
import OrderDeliveryFee from "./components/Pages/Orders/OrderDeliveryFee";
import PlaceOrder from "./components/Pages/Orders/PlaceOrder";
import PaymentPage from "./components/Pages/PaymentPage";
import TrackOrder from "./components/Pages/Orders/TrackOrder";
import SingleProduct from "./components/Pages/Products/SingleProduct";
import FeedPage from "./components/Pages/Feed";
import SearchPage from "./components/Pages/SearchPage";
import CategoryProductsPage from "./components/Pages/Search/CategoryProductsPage";
import SubcategoryProductsPage from "./components/Pages/Search/SubcategoryProductsPage";
import Wallet from "./components/Pages/Wallet";
import TransfersPage from "./components/Pages/Transfers";
import Setting from "./components/Pages/Setting";
import BusinessesPage from "./components/Pages/Businesses/BusinessesPage";
import BusinessProfilePage from "./components/Pages/Businesses/BusinessProfilePage";
import BusinessProductsPage from "./components/Pages/Businesses/BusinessProductsPage";
import Awards from "./components/Pages/Awards";
import ConfirmMpesaPayment from "./components/Forms/ConfirmMpesaPayment";
import OrderHistory from "./components/Pages/Orders/OrderHistory";
import PaymentRequest from "./components/Pages/PaymentRequest";
import Profile from "./components/Pages/Users/Profile";
import EditProfile from "./components/Pages/Users/EditProfile";
import PasswordPage from "./components/Pages/Users/Password";
import Followers from "./components/Pages/Users/Followers";
import ForgotPassword from "./components/Pages/Users/ForgotPassword";
import ResetPassword from "./components/Pages/Users/ResetPassword";
import Notifications from "./components/Pages/Notifications";
import AiCreditWallet from "./components/Pages/Users/AiCreditWallet";
import DepositAiCreditForm from "./components/Forms/DepositAiCreditForm";
import Checkout from "./components/Pages/Checkout";
import SubscriptionsPage from "./components/Pages/SubscriptionsPage";
import ProfileCompanyView from "./components/Pages/Users/ProfileCompanyView";
import EditProductImageForm from "./components/Forms/EditProductImageForm";

const router = app("router");
const h = app("h");
const broker = app("broker");
const $e = app("$e");

ojs(
    App,
    Index,
    LoginPage,
    SignUp,
    CartPage,
    Page404,
    OrderDeliveryFee,
    PlaceOrder,
    PaymentPage,
    TrackOrder,
    SingleProduct,
    FeedPage,
    SearchPage,
    CategoryProductsPage,
    SubcategoryProductsPage,
    Wallet,
    TransfersPage,
    Setting,
    BusinessesPage,
    BusinessProfilePage,
    BusinessProductsPage,
    Awards,
    ConfirmMpesaPayment,
    OrderHistory,
    PaymentRequest,
    Profile,
    EditProfile,
    PasswordPage,
    Followers,
    ForgotPassword,
    ResetPassword,
    Notifications,
    AiCreditWallet,
    DepositAiCreditForm,
    Checkout,
    SubscriptionsPage,
    ProfileCompanyView,
    EditProductImageForm,
);

const renderInApp = (callback) => {
    // resolves the current page in the main content component directly
    resolveCurrentPageUsing(() => callback());

    return h.App(gc.appLoaded, {
        parent: gc.root,
        resetParent: router.reset,
        reconcileParent: true,
    });
};

const middleware = new Middleware();

router.default(() => router.to("page-not-found"));

router.on("/404", () => renderInApp(() => h.Page404()), "page-not-found");

router
    .orOn(
        ["/", "/index"],
        () => {
            setActiveNav('home')
            renderInApp(() => h.Index(gc.auth));
            analytics.pageView("Index");
            fireEvent($e.order.needs.recentOrders);
        },
        ["home", "index"],
    )
    .on(
        "/cart",
        () => {
            setActiveNav('cart')
            middleware.auth(() => {
                renderInApp(() => h.CartPage(uc.cart));
                analytics.pageView("Cart");
            });
        },
        "user.view-cart",
    )
    .on(
        "/checkout",
        () => {
            setActiveNav('cart');
            middleware.auth(() => {
                if (empty(uc.cart.value)) {
                    return redirect().to("user.view-cart");
                }
                renderInApp(() => h.Checkout());
                analytics.pageView(PAGES.CHECKOUT);
            });
        },
        "user.checkout",
    )
    .on(
        "/wallet",
        () => {
            setActiveNav('wallet');
            middleware.auth(() => {
                renderInApp(() => h.Wallet());
                analytics.pageView(PAGES.WALLET);
            });
        },
        "user.view-wallet",
    )
    .on(
        "/settings",
        () => {
            setActiveNav('settings');
            middleware.auth(() => {
                renderInApp(() => h.Setting());
                fireEvent($e.setting.needs.information);
                analytics.pageView(PAGES.SETTINGS);
            });
        },
        "user.settings",
    )
    .on(
        "/business/{businessId}/subcategory/{subcategoryId}/products",
        () => {
            setActiveNav('businesses');
            renderInApp(() => h.BusinessProductsPage());
            analytics.pageView("Business Subcategory Products");
        },
        "business.subcategory.products",
    )
    .on(
        "/business/{businessId}",
        () => {
            setActiveNav('businesses');
            middleware.auth(() => {
                renderInApp(() => h.BusinessProfilePage());
                analytics.pageView("BusinessProfile");
            });
        },
        "business.view-profile",
    )
    .on(
        "/businesses",
        () => {
            setActiveNav('businesses');
            renderInApp(() => h.BusinessesPage());
            analytics.pageView("businesses");
        },
        "companies.list",
    )
    .on("/awards", () => {
        setActiveNav('awards');
        middleware.auth(() => {
            renderInApp(() => h.Awards());
            analytics.pageView(PAGES.AWARDS);
        });
    })
    .on(
        "/login",
        () => {
            setActiveNav('login');
            renderInApp(() => h.LoginPage());
            analytics.pageView("Login");
        },
        "user.login",
    );

router.prefix("payment").group(() => {
    router.on("mpesa/process", () => {
        setActiveNav('cart');
        middleware.auth(() => {
            renderInApp(() => h.ConfirmMpesaPayment());
            analytics.pageView("Mpesa Payment Confirmation");
        });
    });
});

router.prefix("orders").group(() => {
    router.on(
        "{orderId}/track",
        () => {
            setActiveNav('order-history');
            middleware.auth(() => {
                renderInApp(() => h.TrackOrder());
                analytics.pageView("Track Order");
            });
        },
        "order.track",
    );

    router.on(
        "/history",
        () => {
            setActiveNav('order-history');
            middleware.auth(() => {
                renderInApp(() => h.OrderHistory());
                analytics.pageView("Order History");
            });
        },
        "orders.index",
    );

    router.on(
        "select-address",
        () => {
            setActiveNav('cart');
            middleware.auth(() => {
                middleware.profileCompletion(() => {
                    if (empty(uc.cart.value)) {
                        return redirect().to("user.view-cart");
                    }

                    renderInApp(() => h.OrderDeliveryFee());
                });
            });
        },
        "orders.select-address",
    );

    router.on(
        "place",
        () => {
            setActiveNav('cart');
            middleware.auth(() => {
                middleware.profileCompletion(() => {
                    middleware.deliveryAddress(() => {
                        if (
                            empty(uc.cart.value) ||
                            empty(uc.logisticsCompany.value)
                        ) {
                            return redirect().to("user.view-cart");
                        }

                        renderInApp(() => h.PlaceOrder());
                    });
                });
            });
        },
        "orders.place",
    );
});

router.orOn(
    ["payments/pay/{amount}/{currency}", "payment/collect/{amount}/{currency}"],
    () => {
        setActiveNav('cart');
        middleware.auth(() => {
            renderInApp(() => h.PaymentPage());

            analytics.pageView("Collect Payment");
        });
    },
    ["payments.collect"],
);

router.on(
    "/payment-requests",
    () => {
        setActiveNav('wallet');
        middleware.auth(() => {
            renderInApp(() => h.PaymentRequest(uc.paymentRequests));
            fireEvent($e.paymentRequest.needs.allRequests);
            analytics.pageView("Payment Requests");
        });
    },
    "payment-requests",
);

router.prefix("users").group(() => {
    router.on(
        "/profile",
        () => {
            setActiveNav('profile');
            middleware.auth(() => {
                renderInApp(() => h.Profile(gc.user));
                analytics.pageView("View Profile");
            });
        },
        "user.view-profile",
    );

    router.on(
        "/profile/edit",
        () => {
            setActiveNav('profile');
            middleware.auth(() => {
                renderInApp(() => h.EditProfile(gc.user));
                analytics.pageView("Edit Profile");
            });
        },
        "user.edit-profile",
    );

    router.on(
        "/profile/change-password",
        () => {
            setActiveNav('profile');
            middleware.auth(() => {
                renderInApp(() => h.PasswordPage());
                analytics.pageView("Change Password");
            });
        },
        "user.change-password",
    );

    router.on(
        "/sign-up",
        () => {
            setActiveNav('sign-up');
            renderInApp(() => h.SignUp());

            analytics.pageView("Sign Up");
        },
        "user.sign-up",
    );

    router.on(
        "/forgot-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ForgotPassword());
            analytics.pageView("Forgot Password");
        },
        "user.forgot-password",
    );

    router.on(
        "/reset-password",
        () => {
            setActiveNav('login');
            renderInApp(() => h.ResetPassword());
            analytics.pageView("Reset Password");
        },
        "user.reset-password",
    );

    router.on(
        "/capa-transfers",
        () => {
            setActiveNav('wallet');
            middleware.auth(() => {
                renderInApp(() => h.TransfersPage());
                analytics.pageView("Transfer History");
            });
        },
        "user.capa-transfers",
    );

    router.on(
        "/notifications",
        () => {
            setActiveNav('profile');
            middleware.auth(() => {
                renderInApp(() => h.Notifications());
                analytics.pageView("Notifications");
            });
        },
        "user.notifications",
    );

    router.on(
        "/ai-credits",
        () => {
            setActiveNav('profile');
            middleware.auth(() => {
                renderInApp(() => h.AiCreditWallet());
                analytics.pageView("AI Credits");
            });
        },
        "user.ai-credits",
    );

    router.on(
        "subscriptions",
        () => {
            setActiveNav('subscriptions');
            middleware.auth().profileCompletion(() => {
                renderInApp(() => h.SubscriptionsPage());
                analytics.pageView("User Subscriptions");
            });
        },
        "user.subscriptions",
    );
});

router.on(
    "/search",
    () => {
        middleware
            .auth(() => {
                setActiveNav('search')
                setCurrentLocationCoordinates(false, null, true);

                setSearchQuery("");
                renderInApp(() => h.SearchPage());
                analytics.pageView("Search Page");
            });
    },
    "app.search",
);

router.prefix("category").group(() => {
    router.on(
        "/{categoryId}/products",
        () => {
            setActiveNav('search');
            renderInApp(() => h.CategoryProductsPage());
            analytics.pageView("Category Products");
        },
        "category.products",
    );

    router.on(
        "/{categoryId}/all-products",
        () => {
            setActiveNav('search');
            renderInApp(() => h.SubcategoryProductsPage());
            analytics.pageView("Subcategory Products");
        },
        "category.all-products",
    );
});
